import type { AlertCondition, AlarmLevel, SeriesPoint } from '../api/types'
import { MISSING, SENTINEL } from './quality'

export interface SimulationRule {
  condition: AlertCondition
  warn_level: number | null
  alarm_level: number
  hold_s: number
}

export interface RuleTestResult {
  evaluated: boolean
  level: AlarmLevel | null
  reason: string
  metric: number | null
  metricKind: 'value' | 'rate' | 'minutes'
  newest: { ts: string; value: number; quality: number } | null
  earlier: { ts: string; value: number; quality: number } | null
  considered: number
  excluded: number
  held: boolean
  newestLevel: AlarmLevel | null
}

const DAY = 86_400_000
const RANK: Record<string, number> = { clear: 0, warning: 1, alarm: 2 }

/** A read-only preview of conditions.py, not the event reconciliation loop.
 *  It never opens, closes, acknowledges or sends an alarm. */
export function testReadingRule(rule: SimulationRule, points: SeriesPoint[], at: Date,
                                truncated = false): RuleTestResult {
  const empty: RuleTestResult = { evaluated: false, level: null, reason: '', metric: null,
    metricKind: rule.condition === 'rate' ? 'rate' : rule.condition === 'stale' ? 'minutes' : 'value',
    newest: null, earlier: null, considered: 0, excluded: 0, held: false, newestLevel: null }
  const unknown = (reason: string): RuleTestResult => ({ ...empty, reason })
  const now = at.getTime()
  if (!Number.isFinite(now)) return unknown('Choose a valid test time.')
  if (truncated) return unknown('The response is incomplete. Choose a shorter lookback before testing.')
  if (!['above', 'below', 'rate', 'stale'].includes(rule.condition)) {
    return unknown('This condition requires station health data.')
  }
  if (!Number.isFinite(rule.alarm_level) || (rule.warn_level !== null && !Number.isFinite(rule.warn_level))
      || !Number.isFinite(rule.hold_s) || rule.hold_s < 0) return unknown('Enter valid rule levels and hold time.')
  if (rule.condition === 'stale' && (rule.alarm_level <= 0 || (rule.warn_level !== null && rule.warn_level <= 0))) {
    return unknown('Time levels must be greater than zero.')
  }
  if (rule.warn_level !== null && (rule.condition === 'below'
    ? rule.warn_level <= rule.alarm_level : rule.warn_level >= rule.alarm_level)) {
    return unknown('The warning and alarm levels are in the wrong order.')
  }
  const inRange = points.filter((p) => Number.isFinite(Date.parse(p[0])) && Date.parse(p[0]) <= now)
  const usable = inRange.filter((p) => p[1] !== null && Number.isFinite(p[1])
    && ((p[2] ?? 0) & (MISSING | SENTINEL)) === 0)
    .map((p) => ({ ts: p[0], value: p[1]!, quality: p[2] ?? 0, time: Date.parse(p[0]) }))
    .sort((a, b) => a.time - b.time)
  empty.excluded = inRange.length - usable.length
  if (!usable.length) return unknown('No evaluable reading in this lookback.')
  const newest = usable[usable.length - 1]!
  empty.newest = newest
  empty.considered = usable.length
  const falling = rule.condition === 'below'
  const levelFor = (value: number): AlarmLevel | null => {
    if (falling ? value <= rule.alarm_level : value >= rule.alarm_level) return 'alarm'
    if (rule.warn_level !== null && (falling ? value <= rule.warn_level : value >= rule.warn_level)) return 'warning'
    return null
  }
  let metric = newest.value
  let held = false
  let level: AlarmLevel | null
  let newestLevel: AlarmLevel | null = null
  if (rule.condition === 'rate') {
    const candidates = usable.filter((p) => p.time >= newest.time - 2 * DAY && p.time <= newest.time - DAY)
    const start = candidates[candidates.length - 1]
    if (!start) return unknown('A reading 24 to 48 hours before the newest reading is required.')
    empty.earlier = start
    empty.considered = 2
    metric = Math.abs((newest.value - start.value) / ((newest.time - start.time) / DAY))
    level = levelFor(metric)
  } else if (rule.condition === 'stale') {
    metric = (now - newest.time) / 60_000
    empty.considered = 1
    level = levelFor(metric)
  } else {
    level = levelFor(metric)
    newestLevel = level
    if (rule.hold_s > 0) {
      const window = usable.filter((p) => p.time >= now - rule.hold_s * 1000)
      if (!window.length) return unknown('No evaluable reading in the hold window.')
      held = true
      empty.considered = window.length
      level = window.map((p) => levelFor(p.value)).reduce<AlarmLevel | null>((lowest, current) =>
        RANK[current ?? 'clear']! < RANK[lowest ?? 'clear']! ? current : lowest, 'alarm')
    } else empty.considered = 1
  }
  if (!Number.isFinite(metric)) return unknown('The result is outside the supported numeric range.')
  return { ...empty, evaluated: true, level, metric, held, newestLevel,
    reason: level === 'alarm' ? 'Alarm threshold met' : level === 'warning' ? 'Warning threshold met' : 'Trigger conditions not met' }
}
